import React, { useState, useEffect, useMemo, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getAllBlogPosts, BlogPost } from "../utils/blogUtils";
import { useLoading } from "../contexts/LoadingContext";
import "./Blog.css";

const Blog: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const { setLoading: setGlobalLoading } = useLoading();
  // Ref for optimistic tag selection state
  const selectedTagsRef = useRef<Set<string>>(new Set());
  // Derive selected tags directly from URL (single source of truth)
  const selectedTags = useMemo(() => {
    const tags = new Set(searchParams.get("tags")?.split(",").filter(Boolean) || []);
    selectedTagsRef.current = tags; // Keep ref in sync
    return tags;
  }, [searchParams]);

  useEffect(() => {
    let loadingSignaled = false;

    const fetchPosts = async () => {
      setGlobalLoading(true);
      loadingSignaled = true;
      try {
        const data = await getAllBlogPosts();
        setPosts(data);
      } catch (error) {
        console.error("Error fetching blog posts:", error);
      } finally {
        setLoading(false);
        if (loadingSignaled) {
          setGlobalLoading(false);
          loadingSignaled = false;
        }
      }
    };

    fetchPosts();

    return () => {
      if (loadingSignaled) {
        setGlobalLoading(false);
      }
    };
  }, [setGlobalLoading]);


  const toggleTag = (tag: string) => {
    // Read from ref for optimistic immediate state
    const newSet = new Set(selectedTagsRef.current);

    if (newSet.has(tag)) {
      newSet.delete(tag);
    } else {
      newSet.add(tag);
    }

    // Update ref immediately (synchronous)
    selectedTagsRef.current = newSet;

    // Sync to URL (asynchronous)
    if (newSet.size === 0) {
      setSearchParams({}, { replace: true });
    } else {
      setSearchParams({ tags: Array.from(newSet).join(",") }, { replace: true });
    }
  };

  const filteredPosts =
    selectedTags.size === 0
      ? posts
      : posts.filter((post) =>
          Array.from(selectedTags).every((tag) => post.tags.includes(tag))
        );

  if (!loading && posts.length === 0) {
    return (
      <div className="no-posts">
        <p>no posts...</p>
      </div>
    );
  }

  const content = (
    <div className="blog">
      <div className="blog-content">
        <div className="container">
          <div className="posts-grid">
            {filteredPosts.map((post) => (
              <article className="post-card" key={post.slug}>
                <Link to={`/blog/${post.slug}`} className="link">
                  <div className="post-meta">
                    <span className="post-date">{post.date}</span>
                  </div>
                  <h2 className="post-title">{post.title}</h2>
                  <p className="post-excerpt">{post.excerpt}</p>
                </Link>
                <div className="post-tags">
                  {post.tags.map((tag) => (
                    <button
                      key={tag}
                      className={`tag tag-clickable ${
                        selectedTags.has(tag) ? "tag-selected" : ""
                      }`}
                      onPointerDown={() => toggleTag(tag)}
                    >
                      {tag}
                    </button>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return content;
  }

  return content;
};

export default Blog;
